import { useEffect, useState } from 'react';
import { api } from '@/api';
import { format } from 'date-fns';

interface InvoiceListItem {
  id: string;
  invoiceNumber: string;
  period: string;
  periodStart: string;
  periodEnd: string;
  totalHours: number;
  totalAmount: number;
  status: string;
  pdfAvailable: boolean;
  generatedAt: string;
  emailedAt: string | null;
}

interface InvoiceLineItem {
  id: string;
  projectId: string | null;
  projectName: string;
  hours: number;
  amount: number;
}

interface InvoiceDetail extends InvoiceListItem {
  lineItems: InvoiceLineItem[];
  notes: string | null;
}

const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function periodLabel(period: string): string {
  const [y, m] = period.split('-');
  return `${monthNames[parseInt(m,10)-1]} ${y}`;
}
function fmtMoney(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [selected, setSelected] = useState<InvoiceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<InvoiceListItem[]>('/invoices');
        setInvoices(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load invoices');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const openDetail = async (id: string) => {
    setSelected(null);
    try {
      const data = await api.get<InvoiceDetail>(`/invoices/${id}`);
      setSelected(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load invoice');
    }
  };

  const downloadPdf = async (id: string, filename: string) => {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${import.meta.env.PROD ? 'https://ultisim-teams-api.ultisim.workers.dev/api/v1' : '/api/v1'}/invoices/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      setError('Failed to download PDF');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8" /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">My Invoices</h1>
        <p className="text-dark-400 mt-1">Monthly invoices, auto-generated on the 1st of each month based on approved hours.</p>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>
      )}

      {invoices.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-dark-800 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-dark-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No invoices yet</h3>
          <p className="text-dark-400">Your first invoice will be generated on the 1st of next month.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-dark-800/50">
                <th className="text-left px-4 py-3 text-xs font-medium text-dark-400 uppercase tracking-wider rounded-tl-lg">Period</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-dark-400 uppercase tracking-wider">Invoice #</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-dark-400 uppercase tracking-wider">Hours</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-dark-400 uppercase tracking-wider">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-dark-400 uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-dark-400 uppercase tracking-wider rounded-tr-lg">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-dark-800/50">
                  <td className="px-4 py-3 text-dark-200">{periodLabel(inv.period)}</td>
                  <td className="px-4 py-3 text-dark-400 text-sm font-mono">{inv.invoiceNumber}</td>
                  <td className="px-4 py-3 text-right text-dark-200">{inv.totalHours.toFixed(2)}h</td>
                  <td className="px-4 py-3 text-right text-white font-medium">{fmtMoney(inv.totalAmount)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${inv.status === 'emailed' ? 'bg-green-900/30 text-green-400' : inv.status === 'quickbooks_pushed' ? 'bg-blue-900/30 text-blue-400' : 'bg-dark-700 text-dark-300'}`}>
                      {inv.status === 'emailed' ? 'Emailed' : inv.status === 'quickbooks_pushed' ? 'In QuickBooks' : 'Issued'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openDetail(inv.id)} className="text-primary-400 hover:text-primary-300 text-sm mr-3">View</button>
                    {inv.pdfAvailable && (
                      <button onClick={() => downloadPdf(inv.id, `${inv.invoiceNumber}.pdf`)} className="text-primary-400 hover:text-primary-300 text-sm">PDF</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-down" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-white">{periodLabel(selected.period)}</h2>
                <p className="text-dark-400 text-sm font-mono">{selected.invoiceNumber}</p>
                <p className="text-dark-500 text-xs mt-1">
                  {selected.periodStart} → {selected.periodEnd} · Generated {format(new Date(selected.generatedAt), 'MMM d, yyyy')}
                </p>
              </div>
              <button onClick={() => setSelected(null)} className="text-dark-400 hover:text-white">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <table className="w-full mb-4">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-2 text-xs uppercase text-dark-400">Project</th>
                  <th className="text-right py-2 text-xs uppercase text-dark-400">Hours</th>
                  <th className="text-right py-2 text-xs uppercase text-dark-400">Amount</th>
                </tr>
              </thead>
              <tbody>
                {selected.lineItems.map((li) => (
                  <tr key={li.id} className="border-b border-dark-800/50">
                    <td className="py-2 text-dark-200">{li.projectName}</td>
                    <td className="py-2 text-right text-dark-200">{li.hours.toFixed(2)}</td>
                    <td className="py-2 text-right text-dark-200">{fmtMoney(li.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-dark-700">
                  <td className="py-3 font-medium text-dark-200">Total</td>
                  <td className="py-3 text-right text-dark-200">{selected.totalHours.toFixed(2)}</td>
                  <td className="py-3 text-right text-white font-bold text-lg">{fmtMoney(selected.totalAmount)}</td>
                </tr>
              </tfoot>
            </table>

            {selected.pdfAvailable && (
              <button onClick={() => downloadPdf(selected.id, `${selected.invoiceNumber}.pdf`)} className="btn-primary w-full">
                Download PDF
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
